/* eslint-disable no-redeclare */
/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { TFunction } from 'i18next';
import { cloneDeep } from 'lodash';
import type { InitialMapProps } from '@opengeoweb/core';
import { snackbarTypes } from '@opengeoweb/snackbar';
import type { MapPreset } from '@opengeoweb/store';
import { PresetFilterType } from './types';
import { i18n } from '../../utils/i18n';
import type {
  TemplatePresetTitle,
  WorkspaceTemplatePreset,
  WorkspaceChangeToEmptyMapWorkspacePayload,
  WorkspacePreset,
  WorkspacePresetListItem,
  WorkspaceState,
  WorkspaceViewsFromBE,
  WorkspaceViewType,
} from '../workspace/types';
import { WorkspacePresetAction } from '../workspace/types';

export const getSnackbarMessage = (
  action: WorkspacePresetAction,
  title: string,
  is_shared?: boolean,
): snackbarTypes.TranslatableMessage => {
  // Trim and add quotes
  const trimmedTitle = title.trim();
  switch (action) {
    case WorkspacePresetAction.DELETE:
      return {
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        key: 'workspace-list-success-delete',
        params: { TITLE: trimmedTitle },
      };
    case WorkspacePresetAction.DELETE_SYSTEM_PRESET:
      return {
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        key: 'workspace-list-success-delete-system',
        params: {
          TITLE: trimmedTitle,
        },
      };
    case WorkspacePresetAction.DUPLICATE:
      return {
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        key: 'workspace-list-success-duplicate',
        params: { TITLE: trimmedTitle },
      };
    case WorkspacePresetAction.EDIT:
      return {
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        key: 'workspace-list-success-edit',
        params: { TITLE: trimmedTitle },
      };
    case WorkspacePresetAction.RESET:
      return {
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        key: 'workspace-list-success-reset',
        params: {
          TITLE: trimmedTitle,
        },
      };
    case WorkspacePresetAction.SHARE:
      return {
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        key: is_shared
          ? 'workspace-list-success-unshare'
          : 'workspace-list-success-share',
        params: {
          TITLE: trimmedTitle,
        },
      };
    default:
      return {
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        key: 'workspace-list-success-save',
        params: { TITLE: trimmedTitle },
      };
  }
};

export function getEmptyWorkspaceListItem(): Omit<
  WorkspacePresetListItem,
  'title'
>;
export function getEmptyWorkspaceListItem(
  t: TFunction,
): WorkspacePresetListItem;
export function getEmptyWorkspaceListItem(
  t?: TFunction,
): Omit<WorkspacePresetListItem, 'title'> | WorkspacePresetListItem;
export function getEmptyWorkspaceListItem(
  t?: TFunction,
): Omit<WorkspacePresetListItem, 'title'> | WorkspacePresetListItem {
  return {
    id: undefined!,
    title: t ? t('workspace-new') : undefined,
    labels: undefined,
    scope: 'system',
    abstract: '',
    viewType: 'singleWindow',
  };
}

export function getEmptyMapWorkspace(): Omit<WorkspacePreset, 'title'>;
export function getEmptyMapWorkspace(t: TFunction): WorkspacePreset;
export function getEmptyMapWorkspace(
  t?: TFunction,
): WorkspacePreset | Omit<WorkspacePreset, 'title'> {
  return {
    ...getEmptyWorkspaceListItem(t),

    views: {
      allIds: ['1_screen'],
      byId: {
        '1_screen': {
          title: t ? t('workspace-mappreset-new') : undefined,
          componentType: 'Map',
          initialProps: {
            mapPreset: {
              shouldShowLayerManager: true,
              dockedLayerManagerSize: 'sizeSmall',
            },
            syncGroupsIds: [],
          },
        },
      },
    },
    mosaicNode: '1_screen',
  };
}

export const getEmptyWorkspacePreset = (): Omit<WorkspacePreset, 'title'> => ({
  scope: 'system',
  abstract: '',
  views: {
    allIds: ['1_screen'],
    byId: {
      '1_screen': {
        componentType: 'Map',
        initialProps: {
          mapPreset: {
            shouldShowLayerManager: true,
            dockedLayerManagerSize: 'sizeSmall',
          },
          syncGroupsIds: [],
        },
      },
    },
  },
  mosaicNode: '1_screen',
  viewType: 'singleWindow',
  username: 'global',
});

export const createCustomEmptyMapWorkspace = (
  emptyWorkspaceTitle: string,
  emptyMapWorkspaceTitle: string,
  customMapPreset?: MapPreset,
): WorkspacePreset => {
  const customEmptyMapWorkspace = cloneDeep(getEmptyWorkspacePreset());
  if (customMapPreset) {
    (
      customEmptyMapWorkspace.views.byId!['1_screen']
        .initialProps! as InitialMapProps
    ).mapPreset = customMapPreset;
  }

  customEmptyMapWorkspace.views.byId!['1_screen'].title =
    emptyMapWorkspaceTitle;
  const emptyWorkspacePreset: WorkspacePreset = {
    ...customEmptyMapWorkspace,
    title: emptyWorkspaceTitle,
  };
  return emptyWorkspacePreset;
};

const FALLBACK_TEMPLATE_LANGUAGE = 'en';

// Falls back to English and then to the first title, so a template does not
// need a title for every language
export const getTemplatePresetTitle = (
  title: TemplatePresetTitle,
  language: string | undefined = i18n.language,
): string => {
  if (typeof title === 'string') {
    return title;
  }
  const languageCode = [
    language,
    language?.split('-')[0],
    FALLBACK_TEMPLATE_LANGUAGE,
  ].find((code) => code && title[code]);

  return languageCode ? title[languageCode] : (Object.values(title)[0] ?? '');
};

export const findWorkspaceTemplatePreset = (
  defaultPresets: WorkspaceTemplatePreset[] | undefined,
  presetId?: string,
): WorkspaceTemplatePreset | undefined =>
  (defaultPresets ?? []).find(
    (preset) => (preset.id || undefined) === (presetId || undefined),
  );

export const isWorkspaceTemplatePresetId = (
  defaultPresets: WorkspaceTemplatePreset[] | undefined,
  presetId?: string,
): boolean =>
  findWorkspaceTemplatePreset(defaultPresets, presetId) !== undefined;

export const toWorkspacePresetFromTemplate = (
  preset: WorkspaceTemplatePreset,
  language?: string,
): WorkspacePreset => {
  const { views, ...rest } = preset;

  return {
    ...rest,
    title: getTemplatePresetTitle(preset.title, language),
    views: {
      allIds: views.allIds,
      byId: Object.fromEntries(
        views.allIds.map((viewId): [string, WorkspaceViewType] => {
          const view = views.byId[viewId];
          return [
            viewId,
            {
              ...view,
              title:
                view.title === undefined
                  ? undefined
                  : getTemplatePresetTitle(view.title, language),
            },
          ];
        }),
      ),
    },
  };
};

export const getEmptyMapWorkspaceTitles = (
  preset: WorkspaceTemplatePreset,
  language?: string,
): WorkspaceChangeToEmptyMapWorkspacePayload => {
  const { title, views } = toWorkspacePresetFromTemplate(preset, language);

  return {
    newWorkspaceText: title,
    newMapPresetText: views.byId[views.allIds[0]]?.title ?? title,
  };
};

export const toWorkspaceTemplateListItem = (
  preset: WorkspaceTemplatePreset,
  language?: string,
): WorkspacePresetListItem => ({
  id: preset.id!,
  title: getTemplatePresetTitle(preset.title, language),
  labels: preset.labels,
  scope: preset.scope ?? 'system',
  abstract: preset.abstract ?? '',
  viewType: preset.viewType ?? 'singleWindow',
  isWorkspaceTemplate: true,
});

export const toWorkspaceTemplateListItems = (
  defaultPresets: WorkspaceTemplatePreset[] | undefined,
  language?: string,
): WorkspacePresetListItem[] =>
  (defaultPresets ?? []).map((preset) =>
    toWorkspaceTemplateListItem(preset, language),
  );

export const getBEViewsFromState = (
  workspace: WorkspaceState,
): WorkspaceViewsFromBE[] => {
  const result: WorkspaceViewsFromBE[] = [];
  (workspace?.views?.allIds ?? []).forEach((id: string) => {
    const view = workspace.views.byId[id] ?? {};
    result.push({
      mosaicNodeId: id,
      title:
        typeof view.title === 'string' && view.title.trim() ? view.title : id,
      componentType: view.componentType,
      initialProps: view.initialProps,
    });
  });
  return result;
};

export const togglePresetFilter = <
  T extends { id: string; isSelected?: boolean },
>(
  filters: T[],
  id: PresetFilterType,
  isSelected: boolean,
  isAdmin = false,
  isFavouritesEnabled = true,
  isTemplatesEnabled = true,
): T[] => {
  if (!filters.some((filter) => filter.id === id)) {
    return filters;
  }
  const availableFilters = filters.filter((filter) => {
    if (filter.id === PresetFilterType.favourites) {
      return isFavouritesEnabled;
    }
    if (filter.id === PresetFilterType.templates && !isTemplatesEnabled) {
      return false;
    }
    return (
      !isAdmin ||
      filter.id === PresetFilterType.system ||
      filter.id === PresetFilterType.shared ||
      filter.id === PresetFilterType.templates
    );
  });
  const allSelected = availableFilters.every((filter) => filter.isSelected);
  const allUnselected = availableFilters.every((filter) =>
    filter.id === id ? !isSelected : !filter.isSelected,
  );
  return filters.map((filter) => {
    if (allSelected) {
      return { ...filter, isSelected: filter.id === id && isSelected };
    }
    // Admins select one scope at a time, deselecting goes through the
    // regular flow so the last deselected chip falls back to all
    if (
      isAdmin &&
      isSelected &&
      id !== PresetFilterType.favourites &&
      filter.id !== PresetFilterType.favourites
    ) {
      return { ...filter, isSelected: filter.id === id };
    }
    return {
      ...filter,
      isSelected:
        allUnselected || (filter.id === id ? isSelected : filter.isSelected),
    };
  });
};
